import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { setBoardTokenProvider } from '@/lib/api/client';
import { classroomApi } from '@/lib/api/endpoints';
import type { BoardSession } from '@/lib/api/types';
import { storageKeys } from '@/lib/config';
import { readStorage, removeStorage, writeStorage } from '@/lib/storage';

export interface BoardContextValue {
  board: BoardSession | null;
  deviceKey: string;
  isActive: boolean;
  open: (session: BoardSession) => void;
  close: () => Promise<void>;
}

export const BoardContext = createContext<BoardContextValue | null>(null);

const readDeviceKey = (): string => {
  const stored = readStorage<string | null>(storageKeys.trustedDevice, null);
  if (stored) return stored;
  const generated = 'board-device-305';
  writeStorage(storageKeys.trustedDevice, generated);
  return generated;
};

export const BoardProvider = ({ children }: { children: ReactNode }) => {
  const [board, setBoard] = useState<BoardSession | null>(() =>
    readStorage<BoardSession | null>(storageKeys.boardSession, null),
  );
  const [deviceKey] = useState<string>(readDeviceKey);

  useEffect(() => {
    setBoardTokenProvider(
      () => readStorage<BoardSession | null>(storageKeys.boardSession, null)?.board_token ?? null,
    );
  }, []);

  useEffect(() => {
    if (board) writeStorage(storageKeys.boardSession, board);
    else removeStorage(storageKeys.boardSession);
  }, [board]);

  const open = useCallback((session: BoardSession) => {
    writeStorage(storageKeys.boardSession, session);
    setBoard(session);
  }, []);

  const close = useCallback(async () => {
    await classroomApi.boardLogout().catch(() => undefined);
    removeStorage(storageKeys.boardSession);
    setBoard(null);
  }, []);

  const value = useMemo<BoardContextValue>(
    () => ({ board, deviceKey, isActive: board !== null, open, close }),
    [board, close, deviceKey, open],
  );

  return <BoardContext.Provider value={value}>{children}</BoardContext.Provider>;
};
